import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { logout } from "features/auth/authSlice";
import { selectCurrentLanguage } from "features/language/languageSlice";

import { refreshToken, getAccessToken } from "components/utilities/sedra3/auth";

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_BASE_URL,

  prepareHeaders: (headers, { getState }) => {
    const token = getAccessToken();
    const state = getState();
    const currentLanguage = selectCurrentLanguage(state);

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    if (currentLanguage) {
      headers.set("X-Accept-Language", currentLanguage);
    }
    return headers;
  },
});

// Backend 403 details that mean "token is broken — try to refresh".
// Anything else with a 403 status is an authorisation miss (RBAC, scope)
// where refreshing the token will not help — we surface those to the
// caller and skip the refresh+logout pipeline so the user stays logged in.
//
// Source of these strings:
//   * backend/app/api/deps.py (get_current_user)  "Invalid token: …"
//                                                 "Token validation failed."
//   * backend/app/main.py:64                      "Invalid token"
const TOKEN_REFRESH_TRIGGERS = ["Token validation failed", "Invalid token"];

const isTokenRelated403 = (errorPayload) => {
  const detail = errorPayload?.data?.detail;
  if (typeof detail !== "string") return false;
  return TOKEN_REFRESH_TRIGGERS.some((needle) => detail.includes(needle));
};

export const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  // Only run the refresh+logout pipeline on token-related 403s. Authorisation
  // misses (insufficient permissions, tenant violation that wasn't 404-coerced
  // by the backend) must not log the user out — those errors propagate to the
  // calling component which can show a toast or guide the user.
  if (
    result.error &&
    result.error.status === 403 &&
    isTokenRelated403(result.error)
  ) {
    try {
      const newToken = await refreshToken();
      // Retry the original query with the new token
      if (newToken) {
        const retryArgs = {
          ...args,
          headers: {
            ...(args.headers || {}),
            Authorization: `Bearer ${newToken}`,
          },
        };

        result = await baseQuery(retryArgs, api, extraOptions);
      }
    } catch (error) {
      console.error("Token refresh failed", error);
      // The server refuses the session (logout, password reset, expiry). The
      // logout clears the stored tokens and the JWT mirror cookie — left in
      // place, nginx would keep verifying a token whose redis entry is gone and
      // the user would land in a redirect loop — and empties the API caches;
      // the signed-in pages then send the browser to the sign-in page.
      api.dispatch(logout());
    }
  }

  return result;
};

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    loginUser: builder.mutation({
      query: (credentials) => ({
        url: "/login",
        method: "POST",
        body: credentials,
      }),
    }),
    getBanks: builder.query({
      query: () => "/bank/list",
    }),
    getRolesByBank: builder.query({
      query: (bankId) => ({
        url: `/role/${bankId}/roles`,
        method: "GET",
        headers: {
          // Add any custom headers here if needed
        },
      }),
    }),
    registerUser: builder.mutation({
      query: (userDetails) => ({
        url: "/user",
        method: "POST",
        body: userDetails,
      }),
    }),
    openPasswordResetLink: builder.mutation({
      query: (linkToken) => ({
        url: "/login/reset-password/open",
        method: "POST",
        body: linkToken,
      }),
    }),
    resetPassword: builder.mutation({
      query: (passwordResetData) => ({
        url: "/login/reset-password",
        method: "POST",
        body: passwordResetData,
      }),
    }),
    passwordResetRequest: builder.mutation({
      query: (email) => ({
        url: "/login/password-reset-request",
        method: "POST",
        body: { email },
      }),
    }),
    changePassword: builder.mutation({
      query: (passwordChangeData) => ({
        url: "/login/change_password",
        method: "POST",
        body: passwordChangeData,
      }),
    }),
    logoutUser: builder.mutation({
      query: () => ({
        url: "/login/logout",
        method: "POST",
      }),
    }),
  }),
});

export const {
  useLoginUserMutation,
  useRegisterUserMutation,
  useGetBanksQuery,
  useGetRolesByBankQuery,
  useOpenPasswordResetLinkMutation,
  useResetPasswordMutation,
  usePasswordResetRequestMutation,
  useChangePasswordMutation,
  useLogoutUserMutation,
} = authApi;
